import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, FileText, Users as UsersIcon, Activity, ShieldAlert, Plus,
} from 'lucide-react';

const STATUS_BADGE = {
  aktiv: { text: 'Aktiv', bg: '#dcfce7', color: '#166534' },
  inaktiv: { text: 'Inaktiv', bg: '#f3f4f6', color: '#374151' },
  uppsagd: { text: 'Uppsagd', bg: '#fee2e2', color: '#991b1b' },
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('sv-SE');
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('sv-SE');
}

const CustomerDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/customers/${id}/details`);
      setData(r.data);
    } catch (err) {
      toast.error('Kunde inte hämta detaljer');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post(`/admin/customers/${id}/invite-admin`, { email: inviteEmail, name: inviteName });
      toast.success(`Inbjudan skickad till ${inviteEmail}`);
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte bjuda in admin');
    } finally {
      setInviting(false);
    }
  };

  const handleActivateSupport = () => {
    navigate('/admin/support-sessions');
  };

  if (loading) {
    return <DashboardLayout><p className="text-sm text-gray-500">Laddar...</p></DashboardLayout>;
  }
  if (!data) {
    return <DashboardLayout><p className="text-sm text-gray-500">Kund hittades inte.</p></DashboardLayout>;
  }

  const status = STATUS_BADGE[data.status] || STATUS_BADGE.aktiv;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link to="/admin/customers" className="flex items-center gap-1 text-sm text-gray-600 hover:underline">
          <ArrowLeft size={14} /> Tillbaka till kundlistan
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 size={24} />
              {data.name}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm font-mono text-gray-600">{data.org_number || '—'}</span>
              <span
                className="px-2 py-0.5 rounded text-xs font-semibold"
                style={{ background: status.bg, color: status.color }}
              >
                {status.text}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setInviteOpen(true)}
              className="px-3 py-2 text-sm border rounded hover:bg-gray-50 font-semibold"
            >
              + Bjud in administratör
            </button>
            <button
              onClick={() => navigate(`/admin/contracts/new?customer_id=${id}`)}
              className="px-3 py-2 text-sm border rounded hover:bg-gray-50 font-semibold"
            >
              + Nytt avtal
            </button>
            <button
              onClick={handleActivateSupport}
              className="px-3 py-2 text-sm rounded font-semibold text-black"
              style={{ background: '#F9CF3F' }}
            >
              Aktivera supportläge
            </button>
          </div>
        </div>

        {/* Information */}
        <section className="bg-white rounded border p-5">
          <h2 className="font-bold mb-3">Information</h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><dt className="text-gray-500">Org.nummer</dt><dd className="font-mono">{data.org_number || '—'}</dd></div>
            <div><dt className="text-gray-500">Kontaktperson</dt><dd>{data.contact_person || '—'}</dd></div>
            <div><dt className="text-gray-500">E-post</dt><dd>{data.email || '—'}</dd></div>
            <div><dt className="text-gray-500">Telefon</dt><dd>{data.phone || '—'}</dd></div>
            <div><dt className="text-gray-500">Adress</dt><dd>{data.street_address || '—'}</dd></div>
            <div><dt className="text-gray-500">Postnummer & ort</dt><dd>{[data.postal_code, data.city].filter(Boolean).join(' ') || '—'}</dd></div>
            <div><dt className="text-gray-500">Skapad</dt><dd>{formatDate(data.created_at)}</dd></div>
          </dl>
        </section>

        {/* Avtal */}
        <section className="bg-white rounded border p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold flex items-center gap-2"><FileText size={18} /> Avtal</h2>
            <button
              onClick={() => navigate(`/admin/contracts/new?customer_id=${id}`)}
              className="text-sm font-semibold underline"
            >
              + Nytt avtal
            </button>
          </div>
          {data.contracts.length === 0 ? (
            <p className="text-sm text-gray-500">Inga avtal ännu.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left bg-gray-50">
                <tr>
                  <th className="px-3 py-2 font-semibold">Plan</th>
                  <th className="px-3 py-2 font-semibold">Period</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Max anv.</th>
                  <th className="px-3 py-2 font-semibold">Pris/mån</th>
                </tr>
              </thead>
              <tbody>
                {data.contracts.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="px-3 py-2 capitalize">{c.package_code}</td>
                    <td className="px-3 py-2">{formatDate(c.start_date)} – {formatDate(c.end_date)}</td>
                    <td className="px-3 py-2">{c.status}</td>
                    <td className="px-3 py-2">{c.max_users}</td>
                    <td className="px-3 py-2">{c.monthly_price?.toLocaleString('sv-SE') || 0} kr</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Administratörer */}
        <section className="bg-white rounded border p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold flex items-center gap-2"><UsersIcon size={18} /> Administratörer</h2>
            <button
              onClick={() => setInviteOpen(true)}
              className="text-sm font-semibold underline"
            >
              + Bjud in administratör
            </button>
          </div>
          {data.admins.length === 0 ? (
            <p className="text-sm text-gray-500">Inga administratörer. Kunden kan inte logga in förrän en bjuds in.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left bg-gray-50">
                <tr>
                  <th className="px-3 py-2 font-semibold">Namn</th>
                  <th className="px-3 py-2 font-semibold">E-post</th>
                  <th className="px-3 py-2 font-semibold">Senast inloggad</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.admins.map((u) => (
                  <tr key={u.id} className="border-t">
                    <td className="px-3 py-2">{u.name}</td>
                    <td className="px-3 py-2">{u.email}</td>
                    <td className="px-3 py-2">{u.last_login_at ? formatDateTime(u.last_login_at) : 'Aldrig'}</td>
                    <td className="px-3 py-2">{u.is_active ? 'Aktiv' : 'Inaktiv'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Aktivitet senaste 30 dagar */}
        <section className="bg-white rounded border p-5">
          <h2 className="font-bold flex items-center gap-2 mb-3"><Activity size={18} /> Aktivitet (senaste 30 dagar)</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-500">Användare med senaste inloggning</p>
              <p className="text-2xl font-bold">{data.activity?.recent_logins_30d ?? 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Aktiva deltagare</p>
              <p className="text-2xl font-bold">{data.activity?.active_participants ?? 0}</p>
            </div>
          </div>
        </section>

        {/* Supportsessioner */}
        <section className="bg-white rounded border p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold flex items-center gap-2"><ShieldAlert size={18} /> Supportsessioner</h2>
            <button
              onClick={handleActivateSupport}
              className="text-sm font-semibold underline"
            >
              + Aktivera supportläge
            </button>
          </div>
          {data.support_sessions.length === 0 ? (
            <p className="text-sm text-gray-500">Inga supportsessioner mot denna kund.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left bg-gray-50">
                <tr>
                  <th className="px-3 py-2 font-semibold">Anledning</th>
                  <th className="px-3 py-2 font-semibold">Aktiverat</th>
                  <th className="px-3 py-2 font-semibold">Utgår</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Anrop</th>
                </tr>
              </thead>
              <tbody>
                {data.support_sessions.map((s) => (
                  <tr key={s.id} className="border-t">
                    <td className="px-3 py-2 max-w-md truncate" title={s.reason}>{s.reason}</td>
                    <td className="px-3 py-2">{formatDateTime(s.activated_at)}</td>
                    <td className="px-3 py-2">{formatDateTime(s.expires_at)}</td>
                    <td className="px-3 py-2">
                      {s.revoked_at ? 'Avslutad' : s.is_active ? 'Aktiv' : 'Utgången'}
                    </td>
                    <td className="px-3 py-2">{s.access_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Bjud in admin-modal */}
        {inviteOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.25)' }}
            onClick={() => setInviteOpen(false)}
          >
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-2">Bjud in administratör</h2>
              <p className="text-sm text-gray-600 mb-4">
                Användaren får ett mejl med temporärt lösenord.
              </p>
              <form onSubmit={handleInvite} className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold mb-1">Namn *</label>
                  <input
                    required type="text" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">E-post *</label>
                  <input
                    required type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setInviteOpen(false)}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    disabled={inviting}
                    className="px-4 py-2 rounded text-black font-semibold disabled:opacity-50"
                    style={{ background: '#F9CF3F' }}
                  >
                    {inviting ? 'Skickar...' : 'Skicka inbjudan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CustomerDetailsPage;
